import React, {
  ReactNode,
  useContext,
  useId,
  useRef,
  useState,
  createContext,
  Suspense,
  Dispatch,
} from "react";

const DATA_NAME = "__NEXT_DATA_PROMISE__";

type ContextType = {
  values: { [key: string]: { value: unknown } };
  promises: Promise<unknown>[];
  finished: boolean;
};
const promiseContext = createContext<ContextType>(undefined as never);

export const usePromiseState = <T, P extends Promise<T>[] | Promise<T>>(
  p: P | (() => P),
  keyName?: string
) => {
  const context = useContext(promiseContext);
  const id = useId();
  const key = keyName || id;

  const addCache = (p: P | (() => P)): P => {
    const promise = typeof p === "function" ? p() : p;
    if (Array.isArray(promise)) {
      typeof window === "undefined" && context.promises.push(...promise);
      promise.forEach((p) => {
        p.then((value) => {
          context.values[key] = { value };
        });
      });
    } else {
      typeof window === "undefined" && context.promises.push(promise);
      promise.then((value) => {
        context.values[key] = { value };
      });
    }
    return promise;
  };
  const [state, setState] = useState(() => {
    if (typeof window === "undefined") {
      return addCache(p);
    }
    if (context.values[key]) {
      return Promise.resolve(context.values[key].value as T);
    }
    return addCache(p);
  });
  const refState = useRef(state);

  if (refState.current !== state) {
    refState.current = state;
    delete context.values[key];
    addCache(state as P);
  }

  return [state, setState] as [P, Dispatch<P>];
};

const DataRender = () => {
  const context = useContext(promiseContext);
  if (typeof window === "undefined" && !context.finished) {
    const length = context.promises.length;
    throw Promise.all(context.promises).then((v) => {
      if (length === context.promises.length) context.finished = true;
      return v;
    });
  }
  return (
    <script
      id={DATA_NAME}
      type="application/json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(context.values) }}
    />
  );
};

const useContextValue = () => {
  const refContext = useRef<ContextType>({
    values: {},
    promises: [],
    finished: false,
  });
  if (typeof window !== "undefined" && !refContext.current.finished) {
    const node = document.getElementById(DATA_NAME);
    if (node) refContext.current.values = JSON.parse(node.innerHTML);
    refContext.current.finished = true;
  }
  return refContext.current;
};

export const PromiseProvider = ({
  streaming,
  children,
}: {
  streaming?: boolean;
  children: ReactNode;
}) => {
  const value = useContextValue();
  return (
    <promiseContext.Provider value={value}>
      {children}
      {streaming ? (
        <Suspense>
          <DataRender />
        </Suspense>
      ) : (
        <DataRender />
      )}
    </promiseContext.Provider>
  );
};
